import React, { useState, useEffect, useContext, useRef } from "react";
import PersonalDetailsSection from "../Components/PersonalDetails/PersonalDetailsSection";
import { fieldValidations } from "../Components/PersonalDetails/fieldValidations";
import { useSelector, useDispatch } from "react-redux";
import { footerActions } from "../store/footerSlice";
import {
  senderFields,
  receiverFields,
  businessFields,
  collectionBucketFields,
} from "../Components/PersonalDetails/fieldDefinitions";
import { AlertCircle } from "lucide-react";
import { LanguageContext } from "../context/language/LanguageContext";
import appConst from "../Utilities/appConst.json";
import { disableButton, enableButton } from "../Utilities/styleUtil";
import { basketActions } from "../store/basket";
import { validateFields } from "../Components/PersonalDetails/validateFields";
import { fetchStreetOptions } from "../Components/PersonalDetails/fetchStreetOptions";
import { transformData } from "../Components/PersonalDetails/transformData";
import { personalDetailsActions } from "../store/personalDetailsSlice";
import { errorMessages } from "../Components/PersonalDetails/errorMessages";
import { alertActions } from "../store/alertSlice";
import useBrazeScreenView from "../hooks/useBrazeScreenView";
import PageLayout from "../Components/PageLayout";
import { Checkbox } from "../Components/ui/checkbox";

const trimObjectValues = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;

  const trimmed = {};
  for (const [key, value] of Object.entries(obj)) {
    trimmed[key] = typeof value === 'string' ? value.trim() : value;
  }
  return trimmed;
};

const PersonalDetails = () => {
  useBrazeScreenView();
  const dispatch = useDispatch();
  const languageContext = useContext(LanguageContext);

  const cities = useSelector((state) => state.cities.cities);
  const citiesWithIsSpicalCity = useSelector(
    (state) => state.cities.citiesObject
  );
  const shippingMethod = useSelector((state) => state.shipping.selectedId);
  const isCollectionBucketCouponFromShipping = useSelector(
    (state) => state.coupons.isCollectionBucketCouponFromShipping
  );
  const isCheckedOtherReceiver = useSelector(
    (state) => state.personalDetails.isCheckedOtherReceiver
  );
  const isCheckedBusinessInvoice = useSelector(
    (state) => state.personalDetails.isCheckedBusinessInvoice
  );
  const direction = useSelector((state) => state.mode.direction);
  const senderPersonalDetails = useSelector(
    (state) => state.personalDetails.senderPersonalDetails
  );
  const receiverPersonalDetails = useSelector(
    (state) => state.personalDetails.receiverPersonalDetails
  );
  const businessPersonalDetails = useSelector(
    (state) => state.personalDetails.businessPersonalDetails
  );
  const collectionBucketPersonalDetails = useSelector(
    (state) => state.personalDetails.senderPersonalDetails
  );

  const additionalReceiverRef = useRef(null);
  const businessBillingRef = useRef(null);

  const [senderData, setSenderData] = useState(trimObjectValues(senderPersonalDetails));
  const [receiverData, setReceiverData] = useState(trimObjectValues(receiverPersonalDetails));
  const [businessData, setBusinessData] = useState(trimObjectValues(businessPersonalDetails));
  const [collectionBucketData, setCollectionBucketData] = useState(
    trimObjectValues(collectionBucketPersonalDetails)
  );

  const [streetOptions, setStreetOptions] = useState({
    sender: [],
    receiver: [],
    business: [],
    collectionBucket: [],
  });

  const [additionalReceiver, setAdditionalReceiver] = useState(
    isCheckedOtherReceiver
  );
  const [businessBilling, setBusinessBilling] = useState(
    isCheckedBusinessInvoice
  );

  const [errors, setErrors] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [isValid, setIsValid] = useState(true);

  const sectionDataMap = {
    sender: { data: senderData, setData: setSenderData },
    receiver: { data: receiverData, setData: setReceiverData },
    business: { data: businessData, setData: setBusinessData },
    collectionBucket: {
      data: collectionBucketData,
      setData: setCollectionBucketData,
    },
  };

  useEffect(() => {
    dispatch(footerActions.setBeforeContinue(handleSubmit));
    disableButton(dispatch);
  }, []);

  const notOnListMsg = languageContext.getTranslation(
    appConst.VALIDATION_TEXT_NOT_ON_THE_LIST
  );

  const addOptionValidationErrors = (errors, data, section) => {
    ["city", "street"].forEach((field) => {
      if (data[field] && !errors[field]) {
        const opts = getAutoCompleteOptions(field, section);
        if (opts?.length && !opts.includes(data[field])) {
          errors[field] = notOnListMsg;
        }
      }
    });
    return errors;
  };

  useEffect(() => {
    const senderErrors = !isCollectionBucketCouponFromShipping
      ? addOptionValidationErrors(
          getRequierdFieldsErrors(
            validateFields(
              senderData,
              fieldValidations.sender,
              errorMessages.sender,
              languageContext
            )
          ),
          senderData,
          "sender"
        )
      : {};
    const receiverErrors = additionalReceiver
      ? addOptionValidationErrors(
          getRequierdFieldsErrors(
            validateFields(
              receiverData,
              fieldValidations.receiver,
              errorMessages.receiver,
              languageContext
            )
          ),
          receiverData,
          "receiver"
        )
      : {};
    const businessErrors = businessBilling
      ? addOptionValidationErrors(
          getRequierdFieldsErrors(
            validateFields(
              businessData,
              fieldValidations.business,
              errorMessages.business,
              languageContext
            )
          ),
          businessData,
          "business"
        )
      : {};
    const collectionBucketErrors = isCollectionBucketCouponFromShipping
      ? getRequierdFieldsErrors(
          validateFields(
            collectionBucketData,
            fieldValidations.collectionBucket,
            errorMessages.collectionBucket,
            languageContext
          )
        )
      : {};

    if (
      Object.keys(senderErrors).length === 0 &&
      Object.keys(receiverErrors).length === 0 &&
      Object.keys(businessErrors).length === 0 &&
      Object.keys(collectionBucketErrors).length === 0
    ) {
      enableButton(dispatch);
      setIsValid(true);
      dispatch(footerActions.setNextPage(appConst.EIGHTH_PAGE));
    } else {
      disableButton(dispatch);
      setIsValid(false);
      dispatch(footerActions.setNextPage(null));
    }

    // Need to set here the handleSubmit to get there the updated sectionDataMap data
    dispatch(footerActions.setBeforeContinue(handleSubmit));
  }, [errors, additionalReceiver, businessBilling]);

  useEffect(() => {
    // Need to set here the handleSubmit to get there the updated sectionDataMap data
    dispatch(footerActions.setBeforeContinue(handleSubmit));
  }, [isValid]);

  useEffect(() => {
    if (!receiverData.city)
      setReceiverData((prev) => ({ ...prev, street: "" }));
  }, [receiverData.city]);

  // Use useEffect to clear street when city is deleted/cleared
  useEffect(() => {
    if (!senderData.city) {
      setSenderData((prev) => ({ ...prev, street: "" }));
    } else {
      const updateStreetOptions = async () => {
        const streets = await fetchStreetOptions(senderData.city);
        setStreetOptions((prev) => ({
          ...prev,
          sender: streets,
        }));
        // dispatch(loaderActions.loaderHide());
      };

      // dispatch(loaderActions.loaderShow());
      updateStreetOptions();
    }
  }, [senderData.city]);

  useEffect(() => {
    if (!receiverData.city) {
      setReceiverData((prev) => ({ ...prev, street: "" }));
    } else {
      const updateStreetOptions = async () => {
        const streets = await fetchStreetOptions(receiverData.city);
        setStreetOptions((prev) => ({
          ...prev,
          receiver: streets,
        }));
      };

      updateStreetOptions();
    }
  }, [receiverData.city]);

  useEffect(() => {
    if (!businessData.city) {
      setBusinessData((prev) => ({ ...prev, street: "" }));
    } else {
      const updateStreetOptions = async () => {
        const streets = await fetchStreetOptions(businessData.city);
        setStreetOptions((prev) => ({
          ...prev,
          business: streets,
        }));
      };

      updateStreetOptions();
    }
  }, [businessData.city]);

  useEffect(() => {
    if (additionalReceiver) {
      additionalReceiverRef?.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [additionalReceiver]);

  useEffect(() => {
    if (businessBilling) {
      businessBillingRef?.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [businessBilling]);

  // Handle city changes to clear street values
  const handleCityChange = async (newCity, section) => {
    const { data, setData } = sectionDataMap[section];
    setData({
      ...data,
      city: newCity,
      street: "", // Reset the street whenever the city changes
    });
  };

  // Only check for a special area once a city is actually selected from the
  // list (click / Enter) — not while the user is still typing.
  const handleFieldSelect = (field, value, section) => {
    if (
      field === "city" &&
      section != "business" &&
      shippingMethod.toString() === appConst.BHHOME.toString()
    ) {
      checkIsSpecialCity(value);
    }
  };

  const checkIsSpecialCity = (newCity) => {
    const cityObject = citiesWithIsSpicalCity.find(
      (city) => city.city === newCity
    );

    if (cityObject) {
      const isSpecialCity =
        cityObject.is_special_city === appConst.SPECIAL_CITY_CODE;

      if (isSpecialCity) {
        dispatch(
          alertActions.createAlert({
            title: languageContext.getTranslation(appConst.SPECIAL_CITY_TITLE),
            message: languageContext.getTranslation(
              appConst.SPECIAL_CITY_MESSAGE
            ),
            isDialog: false,
          })
        );
      }
    }
  };

  const handleInputChange = (field, value, section) => {
    setShowAlert(false);

    const { setData } = sectionDataMap[section];
    setData((prev) => ({ ...prev, [field]: value }));

    const fieldValidationsList = fieldValidations[section];
    if (fieldValidationsList) {
      const errors = addOptionValidationErrors(
        validateFields(
          { [field]: value },
          fieldValidationsList,
          { [field]: errorMessages[section][field] },
          languageContext
        ),
        { [field]: value },
        section
      );

      setErrors((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: errors[field] || null,
        },
      }));
    }

    if (field === "city") {
      handleCityChange(value, section);
    }
  };

  const getRequierdFieldsErrors = (errors) => {
    if (!errors) return {};
    return Object.fromEntries(
      Object.entries(errors).filter(([_, value]) => value !== undefined)
    );
  };

  const handleSubmit = () => {
    setShowAlert(true);

    if (isValid) {
      const data = isCollectionBucketCouponFromShipping
        ? sectionDataMap["collectionBucket"].data
        : additionalReceiver
        ? sectionDataMap["receiver"].data
        : sectionDataMap["sender"].data;
      const fields = additionalReceiver ? receiverFields : senderFields;
      const newPersonalDetails = transformData(fields, data);
      const invoiceDetails = transformData(
        fields,
        sectionDataMap["sender"].data
      );
      dispatch(basketActions.setDeliveryDetails(newPersonalDetails));
      dispatch(basketActions.setDeliveryInvoiceDetails(invoiceDetails));
      dispatch(
        personalDetailsActions.setIsCheckedOtherReceiver({
          isCheckedOtherReceiver: additionalReceiver,
        })
      );
      dispatch(
        personalDetailsActions.setIsCheckedBusinessInvoice({
          isCheckedBusinessInvoice: businessBilling,
        })
      );
      dispatch(personalDetailsActions.setSenderPersonalDetails(senderData));
      dispatch(personalDetailsActions.setReceiverPersonalDetails(receiverData));
      dispatch(personalDetailsActions.setBusinessPersonalDetails(businessData));
      dispatch(
        personalDetailsActions.setCollectionBucketPersonalDetails(
          collectionBucketData
        )
      );
      if (businessBilling) {
        const data = sectionDataMap["business"].data;
        const fields = businessFields;
        const newCompanyDetails = transformData(fields, data);
        dispatch(basketActions.setCompanyDetails(newCompanyDetails));
      }
    } else {
      const senderErrors = !isCollectionBucketCouponFromShipping
        ? validateFields(
            senderData,
            fieldValidations.sender,
            errorMessages.sender,
            languageContext
          )
        : {};
      const receiverErrors = additionalReceiver
        ? validateFields(
            receiverData,
            fieldValidations.receiver,
            errorMessages.receiver,
            languageContext
          )
        : {};
      const businessErrors = businessBilling
        ? validateFields(
            businessData,
            fieldValidations.business,
            errorMessages.business,
            languageContext
          )
        : {};
      const collectionBucketErrors = isCollectionBucketCouponFromShipping
        ? validateFields(
            collectionBucketData,
            fieldValidations.collectionBucket,
            errorMessages.collectionBucket,
            languageContext
          )
        : {};

      setErrors({
        sender: addOptionValidationErrors(senderErrors, senderData, "sender"),
        receiver: addOptionValidationErrors(receiverErrors, receiverData, "receiver"),
        business: addOptionValidationErrors(businessErrors, businessData, "business"),
        collectionBucket: addOptionValidationErrors(collectionBucketErrors, collectionBucketData, "collectionBucket"),
      });
    }
  };

  const getAutoCompleteOptions = (label, section, options) => {
    if (label === "city") {
      return cities;
    } else if (label === "street") {
      return streetOptions[section];
    } else {
      return options;
    }
  };

  const errorAlertBlock = showAlert && !isValid && (
    <div
      className="personal_details_error_alert"
      role="alert"
      data-testid="personal-details-error-alert"
    >
      <span className="personal_details_error_alert_icon" aria-hidden="true">
        <AlertCircle size={24} />
      </span>
      <span className="personal_details_error_alert_message">
        {languageContext.getTranslation(
          appConst.PERSONAL_DETAILS_ERROR_ALERT
        )}
      </span>
    </div>
  );

  return (
    <>
      {isCollectionBucketCouponFromShipping ? (
        <>
          <div
            className={
              showAlert && !isValid
                ? "hide"
                : "personal_details_collection_bucket_message"
            }
            dir={direction}
            data-testid="personal-details-collection-bucket-message"
          >
            {languageContext.getTranslation(
              appConst.PERSONAL_DETAILS_COLLECTION_BUCKET_MESSAGE
            )}
          </div>
          {errorAlertBlock}
          <PageLayout
            className="personal_details"
            data-testid="personal-details-page"
            style={{
              marginBottom: "1rem",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                direction,
              }}
              data-testid="personal-details-content"
            >
              <div data-testid="personal-details-section-wrapper-collection-bucket">
                <PersonalDetailsSection
                  fields={collectionBucketFields.map((field) => ({
                    ...field,
                    options: getAutoCompleteOptions(
                      field.field,
                      "collectionBucket",
                      field.options
                    ),
                    onChange: (value) => {
                      handleInputChange(field.field, value, "collectionBucket");
                    },
                    onSelect: (value) => {
                      handleFieldSelect(field.field, value, "collectionBucket");
                    },
                    section: "collectionBucket",
                  }))}
                  data={collectionBucketData}
                  errors={errors.collectionBucket || []}
                />
              </div>
            </div>
          </PageLayout>
        </>
      ) : (
        <>
          {errorAlertBlock}
          <PageLayout
            className="personal_details"
            data-testid="personal-details-page"
            style={{
              marginBottom: "1rem",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                direction,
              }}
              data-testid="personal-details-content"
            >
              <div data-testid="personal-details-section-wrapper-sender">
                <PersonalDetailsSection
                  fields={senderFields.map((field) => ({
                    ...field,
                    options: getAutoCompleteOptions(
                      field.field,
                      "sender",
                      field.options
                    ),
                    onChange: (value) => {
                      handleInputChange(field.field, value, "sender");
                    },
                    onSelect: (value) => {
                      handleFieldSelect(field.field, value, "sender");
                    },
                    section: "sender",
                  }))}
                  data={senderData}
                  errors={errors.sender || []}
                />
              </div>

              {!isCollectionBucketCouponFromShipping && (
                <>
                  <hr
                    className="divider mx-4 lg:mx-0"
                    style={{
                      marginTop: "1.5rem",
                      marginBottom: "1.5rem",
                      border: 0,
                      borderTop: "2px solid #dadada",
                    }}
                  />

                  <label
                    htmlFor="business-billing-checkbox"
                    className="inline-flex cursor-pointer items-center gap-[1rem] self-start px-4 lg:px-0"
                    data-testid="personal-details-business-invoice-label"
                  >
                    <Checkbox
                      id="business-billing-checkbox"
                      checked={businessBilling}
                      onCheckedChange={(checked) =>
                        setBusinessBilling(!!checked)
                      }
                      className="h-5 w-5 border-brand [&_svg]:h-3 [&_svg]:w-3"
                      data-testid="personal-details-business-invoice-checkbox"
                    />
                    <span>
                      {languageContext.getTranslation(appConst.BUSINESS_INVOICE)}
                    </span>
                  </label>
                </>
              )}

              {businessBilling && (
                <div
                  ref={businessBillingRef}
                  data-testid="personal-details-section-wrapper-business"
                >
                  <PersonalDetailsSection
                    fields={businessFields.map((field) => ({
                      ...field,
                      options: getAutoCompleteOptions(
                        field.field,
                        "business",
                        field.options
                      ),
                      onChange: (value) => {
                        handleInputChange(field.field, value, "business");
                      },
                      onSelect: (value) => {
                        handleFieldSelect(field.field, value, "business");
                      },
                      section: "business",
                    }))}
                    data={businessData}
                    errors={errors.business || []}
                  />
                </div>
              )}

              {!isCollectionBucketCouponFromShipping && (
                <>
                  <hr
                    className="divider mx-4 lg:mx-0"
                    style={{
                      marginTop: "1.5rem",
                      marginBottom: "1.5rem",
                      border: 0,
                      borderTop: "2px solid #dadada",
                    }}
                  />

                  <label
                    htmlFor="additional-receiver-checkbox"
                    className="inline-flex cursor-pointer items-center gap-[1rem] self-start px-4 lg:px-0"
                    data-testid="personal-details-other-receiver-label"
                  >
                    <Checkbox
                      id="additional-receiver-checkbox"
                      checked={additionalReceiver}
                      onCheckedChange={(checked) =>
                        setAdditionalReceiver(!!checked)
                      }
                      className="h-5 w-5 border-brand [&_svg]:h-3 [&_svg]:w-3"
                      data-testid="personal-details-other-receiver-checkbox"
                    />
                    <span>
                      {languageContext.getTranslation(appConst.OTHER_RECEIVER)}
                    </span>
                  </label>
                </>
              )}

              {additionalReceiver && (
                <div
                  ref={additionalReceiverRef}
                  data-testid="personal-details-section-wrapper-receiver"
                >
                  <PersonalDetailsSection
                    fields={receiverFields.map((field) => ({
                      ...field,
                      options: getAutoCompleteOptions(
                        field.field,
                        "receiver",
                        field.options
                      ),
                      onChange: (value) => {
                        handleInputChange(field.field, value, "receiver");
                      },
                      onSelect: (value) => {
                        handleFieldSelect(field.field, value, "receiver");
                      },
                      section: "receiver",
                    }))}
                    data={receiverData}
                    errors={errors.receiver || []}
                  />
                </div>
              )}
            </div>
          </PageLayout>
        </>
      )}
    </>
  );
};

export default PersonalDetails;
